import { clx } from '@medusajs/ui';
import { format } from 'date-fns';
import { ReactNode } from 'react';

import { useMonthlySettlementInfo } from '@lib/hooks/use-monthly-settlement-info';
import { convertToLocale } from '@lib/util/money';
import { InlineStatusMessage } from '@modules/common/components/inline-status-message';

const convertToTwd = (amount: number) =>
  convertToLocale({
    amount,
    currency_code: 'twd',
  });

interface ThreeColumnItemProps {
  label: string;
  amount: ReactNode;
  amountClassName?: string;
  description?: ReactNode;
}

const ThreeColumnItem = ({
  label,
  amount,
  amountClassName = 'text-blue-30',
  description,
}: ThreeColumnItemProps) => {
  return (
    <div className="md:border-white-40 my-4 px-4 py-2 md:border-r md:last:border-r-0">
      <div>
        <span className="text-black-10 mr-2 text-base">{label}</span>
        <span className={clx('text-xl font-bold', amountClassName)}>
          {amount}
        </span>
      </div>
      {description && (
        <div className="text-grey-60 mt-2 text-sm">{description}</div>
      )}
    </div>
  );
};

export const MonthlySettlementSummary = () => {
  const { data: settlementInfo, isLoading, isError } =
    useMonthlySettlementInfo();

  if (isLoading) return null;

  if (isError) {
    return (
      <div className="mb-8">
        <InlineStatusMessage>
          無法取得結算資訊，請稍後再試
        </InlineStatusMessage>
      </div>
    );
  }

  if (!settlementInfo) {
    return (
      <div className="border-white-40 mb-8 grid min-h-[98px] w-full grid-cols-1 items-center rounded-md border">
        <div className="text-grey-60 h-full px-4 py-6 text-center">
          尚無歷史結算記錄
        </div>
      </div>
    );
  }

  const {
    billingPeriod,
    lastMonthSnapshot,
    currentMonthProgress,
    current,
    pendingRequests,
  } = settlementInfo;
  const hasRepayment = currentMonthProgress.totalRepayment > 0;
  const hasReward = current.rewardBalance !== 0;
  const hasRefund = current.refundBalance !== 0;

  // 計算未出帳金額：當前總欠款 - 上月剩餘應結清
  const unbilledAmount = current.amountDue - current.remainingFromLastMonth;

  return (
    <div className="border-white-40 mb-8 w-full rounded-md border">
      {/* 第一個區塊：上月帳單總覽 */}
      <div className="border-white-40 border-b">
        {/* 區塊標題 */}
        <div className="border-white-40 border-b px-4 py-3">
          <h2 className="text-black-30 text-lg font-semibold">
            {billingPeriod.year} 年 {billingPeriod.month} 月帳單
          </h2>
        </div>
        {/* 三欄式佈局 */}
        <div className="grid grid-cols-1 md:grid-cols-3">
          {/* 帳單金額 */}
          <ThreeColumnItem
            label="帳單金額"
            amount={convertToTwd(lastMonthSnapshot.amountDue)}
            amountClassName="text-orange-30"
            description={
              <>
                <div>截至 {billingPeriod.endDate}</div>
                <div>
                  = 上月賒帳欠款 {convertToTwd(lastMonthSnapshot.loanOutstanding)}
                  {lastMonthSnapshot.cashBalance > 0 &&
                    ` - 預付貨款 ${convertToTwd(lastMonthSnapshot.cashBalance)}`}
                  {lastMonthSnapshot.cashBalance < 0 &&
                    ` + 額外欠款 ${convertToTwd(
                      Math.abs(lastMonthSnapshot.cashBalance)
                    )}`}
                </div>
              </>
            }
          />

          {/* 本月已入金 */}
          <ThreeColumnItem
            label="本月已入金"
            amount={
              hasRepayment
                ? convertToTwd(currentMonthProgress.totalRepayment)
                : '待付款'
            }
            amountClassName={hasRepayment ? 'text-green-600' : 'text-grey-60'}
            description={
              <>
                {hasRepayment && (
                  <div>共 {currentMonthProgress.repaymentCount} 筆入金</div>
                )}
                {pendingRequests.count > 0 && (
                  <div className="text-blue-30">
                    待確認入金共 {pendingRequests.count} 筆，總金額{' '}
                    {convertToTwd(pendingRequests.total)}
                  </div>
                )}
              </>
            }
          />

          {/* 剩餘應結清 */}
          <ThreeColumnItem
            label="剩餘應結清"
            amount={
              current.remainingFromLastMonth > 0
                ? convertToTwd(current.remainingFromLastMonth)
                : '✓ 已結清'
            }
            amountClassName={
              current.remainingFromLastMonth > 0
                ? 'text-orange-30'
                : 'text-green-600'
            }
            description={
              current.remainingFromLastMonth > 0 ? (
                <>
                  = 上月待結清金額 {convertToTwd(lastMonthSnapshot.amountDue)} -
                  本月已入金 {convertToTwd(currentMonthProgress.totalRepayment)}
                </>
              ) : (
                `上月待結清金額 ${convertToTwd(lastMonthSnapshot.amountDue)} 已於本月完成入金`
              )
            }
          />
        </div>
      </div>

      {/* 第二個區塊：當前帳務狀態 */}
      <div className="grid grid-cols-1 md:grid-cols-3">
        {/* 未出帳金額 */}
        <ThreeColumnItem
          label="未出帳金額"
          amount={convertToTwd(unbilledAmount)}
          amountClassName="text-blue-30"
          description="本月未出帳金額（= 當前總欠款 - 上月剩餘應結清），包含本月新增借款及其他未結清項目"
        />

        {/* 當前總欠款 */}
        <ThreeColumnItem
          label="當前總欠款"
          amount={convertToTwd(current.amountDue)}
          amountClassName="text-black-40"
          description={`截至 ${format(new Date(), 'yyyy-MM-dd HH:mm')}`}
        />

        {/* 回饋金 */}
        <div className="h-full px-4 py-6">
          <div className="text-black-10 mb-2 text-base">回饋金</div>
          {hasReward && (
            <div className="mt-2">
              <span className="text-grey-60 mr-2 text-sm">回饋金餘額</span>
              <span className="text-blue-30 text-base font-bold">
                {convertToTwd(current.rewardBalance)}
              </span>
            </div>
          )}
          {hasRefund && (
            <div className="mt-2">
              <span className="text-grey-60 mr-2 text-sm">退貨回饋金餘額</span>
              <span className="text-blue-30 text-base font-bold">
                {convertToTwd(current.refundBalance)}
              </span>
            </div>
          )}
          {!hasReward && !hasRefund && (
            <div className="text-grey-60 text-base">無回饋金</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MonthlySettlementSummary;
